import React from "react"
import { Button } from "reactstrap"
import { useTranslation } from "react-i18next"

const MIN_LENGTH_FOR_MASKING = 2

const maskName = (name, t) => {
  if (!name) return t("Customer")
  const parts = name.split(" ").filter(p => p.length > 0)
  if (parts.length === 0) return t("Customer")
  return parts
    .map(p =>
      p.length > MIN_LENGTH_FOR_MASKING
        ? p[0] + "*".repeat(p.length - MIN_LENGTH_FOR_MASKING) + p[p.length - 1]
        : p
    )
    .join(" ")
}

const StarRating = ({ value }) => {
  const stars = Math.round(value)
  return (
    <span>
      {[1, 2, 3, 4, 5].map(i => (
        <i
          key={i}
          className={`bx ${i <= stars ? "bxs-star text-warning" : "bx-star text-muted"}`}
        ></i>
      ))}
    </span>
  )
}

const RatingsSection = ({
  ratings = [],
  averageRating = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}) => {
  const { t } = useTranslation()

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4 p-3 border rounded bg-light">
        <div className="text-center">
          <h2 className="mb-0 fw-bold">{Number(averageRating).toFixed(1)}</h2>
          <small className="text-muted">{t("out of 5.0")}</small>
        </div>
        <div>
          <StarRating value={averageRating} />
          <p className="mb-0 text-muted small mt-1">
            {t("Based on {{count}} ratings", {
              count: ratings.length > 0 ? `${ratings.length}+` : "0",
            })}
          </p>
        </div>
      </div>

      {ratings.length === 0 ? (
        <div className="text-center py-4 text-muted">{t("No ratings yet")}</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {ratings.map((rating, idx) => (
            <div key={rating._id || idx} className="border rounded p-3">
              <div className="d-flex justify-content-between align-items-start mb-1">
                <div>
                  <strong className="me-2">
                    {maskName(rating.customerName || rating.customer?.name, t)}
                  </strong>
                  <StarRating value={rating.stars ?? rating.rating ?? 0} />
                </div>
                <small className="text-muted">
                  {rating.createdAt
                    ? new Date(rating.createdAt).toLocaleDateString()
                    : ""}
                </small>
              </div>
              {rating.feedback && (
                <p className="mb-1 text-muted small">{rating.feedback}</p>
              )}
              {rating.orderId && (
                <small className="text-muted">
                  {t("Order: #{{orderId}}", { orderId: rating.orderId })}
                </small>
              )}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <Button
            color="light"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <i className="bx bx-chevron-left"></i> {t("Previous")}
          </Button>
          <small className="text-muted">
            {t("Page {{current}} of {{total}}", {
              current: currentPage,
              total: totalPages,
            })}
          </small>
          <Button
            color="light"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            {t("Next")} <i className="bx bx-chevron-right"></i>
          </Button>
        </div>
      )}
    </div>
  )
}

export default RatingsSection
